import {
  DrawerNavigationProp,
  createDrawerNavigator,
} from "@react-navigation/drawer";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { OrgUsersScreen } from "./OrgUsers/OrgUsers";
import { SectionList } from "react-native";
import { useCallback } from "react";
import {
  AHIcon,
  AHIconType,
  Box,
  ListItem,
  useBoxStyle,
  useLayoutContext,
  useTheme,
} from "arkh";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { OrgAdminGeneralScreen } from "./OrgAdminGeneral";
import { OrgAdminStructureScreen } from "./OrgAdminStructure";
import { OrgAdminDeveloperScreen } from "./OrgAdminDeveloper";
import { WebModalHeader } from "../WorkspaceTodos/WorkspaceTodoEdit/WorkspaceTodoEditMain/WebModalHeader";
import { OrgAdminTopicsScreen } from "../OrgAdminTopics/OrgAdminTopics";
import { OrgAdminTeamsScreen } from "./OrgAdminTeams/OrgAdminTeams";
import { OrgAdminDevicesScreen } from "./OrgAdminDevices/OrgAdminDevices";

type OrgAdminDrawerParamList = {
  OrgAdminUserAdmin: object;
  OrgAdminGeneral: object;
  OrgAdminStructure: object;
  OrgAdminDeveloper: object;
  OrgAdminTopics: object;
  OrgAdminTeams: object;
  OrgAdminDevices: object;
};
const OrgAdminDrawer = createDrawerNavigator<OrgAdminDrawerParamList>();

export const OrgAdmin = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"OrgAdmin">) => {
    const intl_tokens = useIntlTokens();
    const theme = useTheme();
    return (
      <>
        <WebModalHeader
          title={
            <WebModalHeader.Title>{intl_tokens.OrgAdmin}</WebModalHeader.Title>
          }
          close={
            <WebModalHeader.CloseButton onPress={props.navigation.goBack} />
          }
        />
        <OrgAdminDrawer.Navigator
          defaultStatus="open"
          drawerContent={(props) => {
            const selectedRouteName = props.state.routeNames[props.state.index];

            return (
              <OrgAdminMasterComponent
                selected={selectedRouteName}
                onSelect={(item) => {
                  props.navigation.navigateDeprecated(item);
                }}
              />
            );
          }}
          screenOptions={{
            headerShown: false,
            drawerType: "permanent",
            drawerStyle: {
              width: 250,
            },
            sceneStyle: {
              backgroundColor: theme["surface-background-light"],
            },
            headerStatusBarHeight: 0,
          }}
        >
          <OrgAdminDrawer.Screen
            name="OrgAdminGeneral"
            component={OrgAdminGeneralScreen}
          />
          <OrgAdminDrawer.Screen
            name="OrgAdminUserAdmin"
            component={OrgUsersScreen}
          />
          <OrgAdminDrawer.Screen
            name="OrgAdminStructure"
            component={OrgAdminStructureScreen}
          />
          <OrgAdminDrawer.Screen
            name="OrgAdminTeams"
            component={OrgAdminTeamsScreen}
          />
          <OrgAdminDrawer.Screen
            name="OrgAdminDevices"
            component={OrgAdminDevicesScreen}
          />
          <OrgAdminDrawer.Screen
            name="OrgAdminTopics"
            component={OrgAdminTopicsScreen}
          />
          <OrgAdminDrawer.Screen
            name="OrgAdminDeveloper"
            component={OrgAdminDeveloperScreen}
          />
        </OrgAdminDrawer.Navigator>
      </>
    );
  },
  undefined,
  {
    width: 900,
    height: 850,
  }
);

type OrgAdminNavigationProp<T extends keyof OrgAdminDrawerParamList> =
  CompositeNavigationProp<
    DrawerNavigationProp<OrgAdminDrawerParamList, T>,
    UserLoggedInNavigatorNavigationProp<"OrgAdmin">
  >;

type OrgAdminRouteProp<T extends keyof OrgAdminDrawerParamList> = RouteProp<
  OrgAdminDrawerParamList,
  T
>;

export type OrgAdminScreenProps<T extends keyof OrgAdminDrawerParamList> = {
  navigation: OrgAdminNavigationProp<T>;
  route: OrgAdminRouteProp<T>;
};

function OrgAdminMasterComponent(props: {
  selected?: string;
  chevron?: boolean;
  onSelect: (key: string) => void;
}) {
  const renderSectionFooter = useCallback(() => <Box spaceTop="large" />, []);

  const style = useBoxStyle({ color: "surface-primary" });
  const layout = useLayoutContext();
  const intl_tokens = useIntlTokens();
  return (
    <Box
      style={{ flex: 1, borderRightWidth: 1 }}
      borderColor="border-primary"
      color="surface-primary"
    >
      <SectionList
        sections={[
          {
            data: [
              {
                title: intl_tokens.General,
                key: "OrgAdminGeneral",
                icon: "settings" satisfies AHIconType,
              },
              {
                title: "Workspaces",
                key: "OrgAdminStructure",
                icon: "workspace" satisfies AHIconType,
              },
              {
                title: intl_tokens.Users,
                key: "OrgAdminUserAdmin",
                icon: "user" satisfies AHIconType,
              },
              {
                title: intl_tokens.Teams,
                key: "OrgAdminTeams",
                icon: "team" satisfies AHIconType,
              },
              {
                title: intl_tokens.Devices,
                key: "OrgAdminDevices",
                icon: "tv" satisfies AHIconType,
              },
              {
                title: intl_tokens.Topics,
                key: "OrgAdminTopics",
                icon: "topic" satisfies AHIconType,
              },

              {
                title: "Developers",
                key: "OrgAdminDeveloper",
                icon: "developer" satisfies AHIconType,
              },
            ] as const,
          },
        ]}
        keyExtractor={(item) => item.key}
        style={style}
        contentContainerStyle={{
          paddingTop: layout.space.medium,
        }}
        renderSectionFooter={renderSectionFooter}
        renderItem={({ index, section, item }) => (
          <ListItem
            isFirst={index === 0}
            isLast={index === section.data.length - 1}
            onPress={() => {
              props.onSelect(item.key);
            }}
            isSelected={props.selected === item.key}
            left={<AHIcon type={item.icon} color="darkerText" size="small" />}
            right={props.chevron ? <ListItem.NavArrow /> : null}
          >
            <ListItem.Title>{item.title}</ListItem.Title>
          </ListItem>
        )}
      />
    </Box>
  );
}
